import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../hooks/useAuth';
import pettyCashService from '../../../services/pettyCashService';

const PettyCashPanel = ({ onAddExpense, onAddReplenishment, onViewTransactions }) => {
  const { userProfile, hasOrganization, isAuthenticated, loading: authLoading } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const organizationId = userProfile?.organization_id;

  // Load petty cash summary
  const loadSummary = async () => {
    // Wait for authentication to complete
    if (authLoading) {
      return;
    }

    // Check authentication and organization status
    if (!isAuthenticated) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    if (!hasOrganization || !organizationId) {
      setError('No organization found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const summaryData = await pettyCashService?.getPettyCashSummary(organizationId);
      setSummary(summaryData);
    } catch (err) {
      console.error('Error loading petty cash summary:', err);
      setError(err?.message || 'Failed to load petty cash data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, [organizationId, isAuthenticated, hasOrganization, authLoading]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Show loading while authentication is still initializing
  if (authLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded"></div>
            <div className="h-4 bg-muted rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show authentication required message
  if (!isAuthenticated) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center">
          <div className="w-12 h-12 bg-info/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-info text-lg">🔐</span>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Authentication Required</h3>
          <p className="text-muted-foreground text-sm mb-4">
            Please sign in to access petty cash management.
          </p>
          <Button
            variant="default"
            size="sm"
            onClick={() => window.location.href = '/login'}
            iconName="LogIn"
            iconPosition="left"
          >
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  // Show organization setup message
  if (!hasOrganization || !organizationId) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center">
          <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-warning text-lg">🏢</span>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Organization Required</h3>
          <p className="text-muted-foreground text-sm mb-4">
            You need to be a member of an organization to manage petty cash transactions.
          </p>
          <div className="flex justify-center space-x-3">
            <Button
              variant="default"
              size="sm"
              onClick={() => window.location.href = '/organization-settings'}
              iconName="Settings"
              iconPosition="left"
            >
              Setup Organization
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadSummary}
              iconName="RefreshCw"
              iconPosition="left"
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded"></div>
            <div className="h-4 bg-muted rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && error !== 'No organization found') {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center">
          <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-error text-lg">⚠️</span>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Unable to Load Petty Cash</h3>
          <p className="text-muted-foreground text-sm mb-4">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={loadSummary}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Petty Cash Management</h2>
            <p className="text-muted-foreground text-sm mt-1">
              Account: {summary?.summary?.accountCode} - {summary?.summary?.accountName}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadSummary}
              iconName="RefreshCw"
              iconPosition="left"
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Current Balance */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Current Balance</p>
                <p className="text-2xl font-bold text-blue-900">
                  {formatCurrency(summary?.currentBalance)}
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-lg">💰</span>
              </div>
            </div>
          </div>

          {/* Monthly Expenses */}
          <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-600 text-sm font-medium">Monthly Expenses</p>
                <p className="text-2xl font-bold text-red-900">
                  {formatCurrency(summary?.monthlyExpenses)}
                </p>
              </div>
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-lg">📉</span>
              </div>
            </div>
          </div>

          {/* Monthly Replenishments */}
          <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Monthly Replenishments</p>
                <p className="text-2xl font-bold text-green-900">
                  {formatCurrency(summary?.monthlyReplenishments)}
                </p>
              </div>
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-lg">📈</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Button
            variant="default"
            onClick={() => onAddExpense?.()}
            iconName="Minus"
            iconPosition="left"
            className="w-full"
          >
            Record Expense
          </Button>
          <Button
            variant="outline"
            onClick={() => onAddReplenishment?.()}
            iconName="Plus"
            iconPosition="left"
            className="w-full"
          >
            Add Replenishment
          </Button>
          <Button
            variant="outline"
            onClick={() => onViewTransactions?.()}
            iconName="List"
            iconPosition="left"
            className="w-full"
          >
            View All Transactions
          </Button>
        </div>

        {/* Recent Transactions */}
        {summary?.recentTransactions?.length > 0 && (
          <div className="bg-muted/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Recent Transactions</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewTransactions?.()}
              >
                View All
              </Button>
            </div>
            <div className="space-y-3">
              {summary?.recentTransactions?.map((transaction) => (
                <div
                  key={transaction?.id}
                  className="flex items-center justify-between p-3 bg-card border border-border rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      transaction?.transactionType === 'expense' ?'bg-red-100 text-red-600' :'bg-green-100 text-green-600'
                    }`}>
                      {transaction?.transactionType === 'expense' ? '↑' : '↓'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {transaction?.description}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(transaction?.entryDate)} • {transaction?.entryNumber}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-semibold ${
                      transaction?.transactionType === 'expense' ? 'text-error' : 'text-success'
                    }`}>
                      {transaction?.transactionType === 'expense' ? '-' : '+'}
                      {formatCurrency(Math.abs(transaction?.amount))}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {transaction?.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State for Recent Transactions */}
        {summary?.recentTransactions?.length === 0 && (
          <div className="bg-muted/20 rounded-lg p-6 text-center">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-muted-foreground text-lg">📝</span>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No Recent Transactions</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Start by recording your first petty cash expense or replenishment.
            </p>
            <div className="flex justify-center space-x-3">
              <Button
                variant="default"
                size="sm"
                onClick={() => onAddExpense?.()}
                iconName="Minus"
                iconPosition="left"
              >
                Record Expense
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAddReplenishment?.()}
                iconName="Plus"
                iconPosition="left"
              >
                Add Replenishment
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PettyCashPanel;